import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Baby, FileText, Printer, FlaskConical, HeartPulse, IndianRupee, Package, Pencil, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill, KpiCard } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { HospitalInvoiceDoc, PrintPreview, MaternityDoc } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { dmy, titleize, num, today, inr } from "@/lib/format";

const EMPTY_BABY = { name: "", gender: "female", weight_kg: "", birth_time: "", apgar_1: "", apgar_5: "", condition: "", notes: "", create_patient: true };
const EMPTY_DIRECT_DELIVERY = {
  patient_mode: "existing",
  patient_id: "",
  new_patient: { name: "", gender: "female", phone: "", dob: "", city: "", address: "",
    guardian_relation: "husband", guardian_name: "", emergency_contact_name: "", emergency_contact_phone: "" },
  lmp_date: "",
  gravida: 1,
  para: 0,
  living_children: 0,
  abortions: 0,
  blood_group: "",
  high_risk: false,
  obstetric_history: "",
  medical_history: "",
  bed_id: "",
  nurse_user_id: "",
  discount: "",
  discount_type: "amount",
  tax_percent: "",
  doctor_id: "",
  package_id: "",
  delivery_type: "normal",
  delivery_at: "",
  outcome: "live_birth",
  indication: "",
  complications: "",
  attending_doctor: "",
  notes: "",
  post_delivery_notes: "",
  babies: [{ ...EMPTY_BABY }],
  initial_payments: [{ amount: "", mode: "cash", reference_no: "" }],
};

const eddFromLmp = (lmp) => {
  if (!lmp) return "";
  const d = new Date(lmp);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + 280);
  return d.toISOString().slice(0, 10);
};

export default function Maternity() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", lmp_date: "", edd: "", gravida: 1, para: 0,
    living_children: 0, abortions: 0, blood_group: "", allergies: "", high_risk: false,
    obstetric_history: "", medical_history: "", antenatal_notes: "", doctor_id: "", doctor_name: "" });
  const [formError, setFormError] = useState("");
  const [modal, setModal] = useState(null); // {kind, record}
  const [visit, setVisit] = useState({ weight: "", bp: "", haemoglobin: "", fundal_height: "", fetal_heart_rate: "", symptoms: "", examination: "", notes: "" });
  const [inv, setInv] = useState({ kind: "blood", name: "", result: "", notes: "", taken_on: today() });
  const [labour, setLabour] = useState({ labour_status: "", cervical_findings: "", fetal_monitoring: "", notes: "" });
  const [delivery, setDelivery] = useState({ delivery_type: "normal", delivery_at: "", outcome: "live_birth",
    indication: "", complications: "", attending_doctor: "", notes: "", post_delivery_notes: "", babies: [{ ...EMPTY_BABY }] });
  const [pp, setPp] = useState({ observations: "", bleeding: "", pain: "", medications: "", breastfeeding: "", discharge_ready: false });
  const [dis, setDis] = useState({ mother_summary: "", mother_medications: "", mother_follow_up: "",
    baby_condition: "", baby_feeding: "", baby_vaccination: "", baby_follow_up: "" });
  const [preview, setPreview] = useState(null);
  const [previewKind, setPreviewKind] = useState("case");
  const canPackages = can("hospital.maternity.packages");
  const [packages, setPackages] = useState([]);
  const [extraServices, setExtraServices] = useState([]);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgTab, setPkgTab] = useState("packages");
  const EMPTY_PKG = { package_id: "", name: "", price: "", delivery_type: "normal", room_category: "", inclusions: "", description: "" };
  const EMPTY_SVC = { service_id: "", name: "", charge: "", tax_percent: "", description: "" };
  const [pkgForm, setPkgForm] = useState(EMPTY_PKG);
  const [svcForm, setSvcForm] = useState(EMPTY_SVC);
  const [billFor, setBillFor] = useState(null);
  const [billData, setBillData] = useState(null);
  const [extraForm, setExtraForm] = useState({ service_id: "", description: "", amount: "", quantity: 1 });
  const [payForm, setPayForm] = useState({ amount: "", mode: "cash", reference_no: "" });
  const [directOpen, setDirectOpen] = useState(false);
  const [directForm, setDirectForm] = useState(EMPTY_DIRECT_DELIVERY);
  const [beds, setBeds] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editClinical, setEditClinical] = useState(null);
  const [editClinicalForm, setEditClinicalForm] = useState({ diagnosis: "", symptoms: "", examination: "", notes: "", obstetric_history: "", medical_history: "", antenatal_notes: "" });
  const [editClinicalBusy, setEditClinicalBusy] = useState(false);
  const [reverseExtra, setReverseExtra] = useState(null);
  const [reverseExtraReason, setReverseExtraReason] = useState("");
  const [nurseFor, setNurseFor] = useState(null);
  const [nurseHistory, setNurseHistory] = useState([]);
  const [nurseForm, setNurseForm] = useState({ staff_user_id: "", role_label: "nurse", reason: "" });
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/hospital/maternity");
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, []);

  const loadMasters = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/hospital/maternity-packages"),
        api.get("/hospital/maternity-extra-services")]);
      setPackages(p.data.items); setExtraServices(s.data.items);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => {
    if (!can("hospital.maternity.manage")) return;
    load();
    loadMasters();
    Promise.all([api.get("/hospital/patients", { params: { limit: 300 } }), api.get("/hospital/doctors")])
      .then(([p, d]) => { setPatients(p.data.items.filter((x) => x.gender === "female")); setDoctors(d.data.items); })
      .catch(() => {});
    api.get("/hospital/wards").then(({ data }) => {
      const avail = (data.items || []).flatMap((w) => (w.beds || [])
        .filter((b) => b.status === "available")
        .map((b) => ({ ...b, ward_name: w.name })));
      setBeds(avail);
    }).catch(() => {});
    api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
  }, [load, loadMasters, can]);

  if (!can("hospital.maternity.manage")) return <NoAccess what="the maternity module" />;

  const run = async (fn, message) => {
    try { await fn(); toast.success(message); setModal(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const create = async (e) => {
    e.preventDefault(); setFormError("");
    if (!form.patient_id) { setFormError("Select the mother's patient record."); return; }
    if (!form.lmp_date) { setFormError("LMP date is required."); return; }
    try {
      const doctor = doctors.find((d) => d.doctor_id === form.doctor_id);
      await api.post("/hospital/maternity", { ...form,
        edd: form.edd || eddFromLmp(form.lmp_date),
        doctor_name: doctor ? doctor.name : "",
        gravida: Number(form.gravida || 0), para: Number(form.para || 0),
        living_children: Number(form.living_children || 0), abortions: Number(form.abortions || 0) });
      toast.success("Maternity case opened.");
      setOpen(false); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const openPreview = async (rec, kind) => {
    try {
      const { data } = await api.get(`/hospital/maternity/${rec.maternity_id}`);
      setPreview(data); setPreviewKind(kind);
    } catch (e) { toast.error(apiError(e)); }
  };

  const gaWeeks = (lmp) => {
    if (!lmp) return "—";
    const days = Math.floor((Date.now() - new Date(lmp).getTime()) / 86400000);
    if (days < 0 || days > 400) return "—";
    return `${Math.floor(days / 7)}w ${days % 7}d`;
  };

  const openBill = async (rec) => {
    setBillFor(rec); setBillData(null);
    setExtraForm({ service_id: "", description: "", amount: "", quantity: 1 });
    setPayForm({ amount: "", mode: "cash", reference_no: "" });
    try { const { data } = await api.get(`/hospital/maternity/${rec.maternity_id}`); setBillData(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const refreshBill = async () => {
    if (!billFor) return;
    try { const { data } = await api.get(`/hospital/maternity/${billFor.maternity_id}`); setBillData(data); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const assignPackage = async (pid) => {
    if (!pid) return;
    try { await api.post(`/hospital/maternity/${billFor.maternity_id}/assign-package`, { package_id: pid });
      toast.success("Package assigned."); refreshBill(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addExtra = async (e) => {
    e.preventDefault();
    if (!Number(extraForm.amount)) { toast.error("Enter the extra service amount."); return; }
    try {
      await api.post(`/hospital/maternity/${billFor.maternity_id}/extras`, {
        service_id: extraForm.service_id, description: extraForm.description,
        amount: Number(extraForm.amount || 0), quantity: Number(extraForm.quantity || 1) });
      toast.success("Extra service added.");
      setExtraForm({ service_id: "", description: "", amount: "", quantity: 1 }); refreshBill();
    } catch (err) { toast.error(apiError(err)); }
  };
  const genInvoice = async () => {
    try { await api.post(`/hospital/maternity/${billFor.maternity_id}/generate-invoice`);
      toast.success("Maternity bill generated."); refreshBill(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const collectPayment = async (e) => {
    e.preventDefault();
    const inv = billData?.invoice;
    if (!inv) { toast.error("Generate the bill first."); return; }
    try {
      await api.post(`/hospital/invoices/${inv.invoice_id}/payments`, {
        amount: Number(payForm.amount || 0), mode: payForm.mode, reference_no: payForm.reference_no });
      toast.success("Payment recorded."); setPayForm({ amount: "", mode: "cash", reference_no: "" }); refreshBill();
    } catch (err) { toast.error(apiError(err)); }
  };
  const savePackage = async (e) => {
    e.preventDefault();
    const body = { name: pkgForm.name, price: Number(pkgForm.price || 0),
      delivery_type: pkgForm.delivery_type, room_category: pkgForm.room_category,
      description: pkgForm.description,
      inclusions: pkgForm.inclusions.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (pkgForm.package_id) await api.patch(`/hospital/maternity-packages/${pkgForm.package_id}`, body);
      else await api.post("/hospital/maternity-packages", body);
      toast.success("Package saved."); setPkgForm(EMPTY_PKG); loadMasters();
    } catch (err) { toast.error(apiError(err)); }
  };
  const saveService = async (e) => {
    e.preventDefault();
    const body = { name: svcForm.name, charge: Number(svcForm.charge || 0),
      tax_percent: Number(svcForm.tax_percent || 0), description: svcForm.description };
    try {
      if (svcForm.service_id) await api.patch(`/hospital/maternity-extra-services/${svcForm.service_id}`, body);
      else await api.post("/hospital/maternity-extra-services", body);
      toast.success("Extra service saved."); setSvcForm(EMPTY_SVC); loadMasters();
    } catch (err) { toast.error(apiError(err)); }
  };
  const pickExtraService = (id) => {
    const svc = extraServices.find((s) => s.service_id === id);
    setExtraForm((f) => ({ ...f, service_id: id,
      description: svc ? svc.name : f.description, amount: svc ? svc.charge : f.amount }));
  };

  const submitDirectDelivery = async (event) => {
    event.preventDefault();
    const isNew = directForm.patient_mode === "new";
    if (isNew) {
      if (!directForm.new_patient.name || !directForm.new_patient.city || !directForm.new_patient.phone) {
        toast.error("Enter the new mother's name, mobile and city."); return;
      }
    } else if (!directForm.patient_id) {
      toast.error("Select the mother's patient record."); return;
    }
    if (!directForm.package_id || !directForm.delivery_at) {
      toast.error("Select the package and enter the delivery time."); return;
    }
    try {
      const payload = {
        ...directForm,
        patient_id: isNew ? "" : directForm.patient_id,
        new_patient: isNew ? { ...directForm.new_patient, gender: "female" } : null,
        gravida: Number(directForm.gravida || 0),
        para: Number(directForm.para || 0),
        living_children: Number(directForm.living_children || 0),
        abortions: Number(directForm.abortions || 0),
        discount: Number(directForm.discount || 0),
        tax_percent: directForm.tax_percent === "" ? null : Number(directForm.tax_percent),
        delivery_at: new Date(directForm.delivery_at).toISOString(),
        babies: directForm.babies.filter((baby) => baby.gender),
        initial_payments: directForm.initial_payments
          .filter((payment) => Number(payment.amount) > 0)
          .map((payment) => ({ ...payment, amount: Number(payment.amount) })),
      };
      delete payload.patient_mode;
      const { data } = await api.post("/hospital/maternity/direct-delivery", payload);
      const detail = await api.get(`/hospital/maternity/${data.maternity.maternity_id}`);
      toast.success(`Delivery and invoice ${data.invoice.invoice_no} recorded${data.patient?.uhid ? ` · UHID ${data.patient.uhid}` : ""}.`);
      setReceipt(detail.data);
      setDirectOpen(false);
      setDirectForm(EMPTY_DIRECT_DELIVERY);
      load();
    } catch (requestError) {
      toast.error(apiError(requestError));
    }
  };

  const openNurse = async (rec) => {
    try {
      const { data } = await api.get(`/hospital/maternity/${rec.maternity_id}/staff-assignments`);
      setNurseFor(rec); setNurseHistory(data.items || []);
      setNurseForm({ staff_user_id: "", role_label: "nurse", reason: "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitNurse = async (event) => {
    event.preventDefault();
    if (!nurseForm.staff_user_id) { toast.error("Select a staff member to assign."); return; }
    try {
      await api.post(`/hospital/maternity/${nurseFor.maternity_id}/staff-assignments`, nurseForm);
      toast.success("Care staff assigned. Previous assignment retained in history.");
      openNurse(nurseFor);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitReverseExtra = async (event) => {
    event.preventDefault();
    if (reverseExtraReason.trim().length < 3) { toast.error("A reversal reason is required."); return; }
    try {
      await api.post(`/hospital/maternity/${billFor.maternity_id}/extras/${reverseExtra.extra_id}/reverse`,
        { reason: reverseExtraReason.trim() });
      toast.success("Extra reversed. Original entry preserved in history.");
      setReverseExtra(null); setReverseExtraReason(""); refreshBill();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="maternity-page">
      <PageHeader overline="Hospital · IPD" title="Maternity & obstetrics"
        subtitle="Antenatal profile, ANC visits, investigations, labour, delivery, newborn linkage, postpartum care and printable records."
        actions={<>
          {canPackages && <button onClick={() => { setPkgTab("packages"); setPkgForm(EMPTY_PKG); setSvcForm(EMPTY_SVC); setPkgOpen(true); }} className={ghostBtnCls} data-testid="manage-packages-btn"><Package className="h-4 w-4" strokeWidth={1.8} /> Manage packages</button>}
          <button
            onClick={() => {
              setDirectForm({
                ...EMPTY_DIRECT_DELIVERY,
                delivery_at: new Date().toISOString().slice(0, 16),
                babies: [{ ...EMPTY_BABY }],
                initial_payments: [{ amount: "", mode: "cash", reference_no: "" }],
              });
              setDirectOpen(true);
            }}
            className={ghostBtnCls}
            data-testid="direct-delivery-btn"
          >
            <Baby className="h-4 w-4" strokeWidth={1.8} /> Direct delivery
          </button>
          <button onClick={() => setOpen(true)} className={primaryBtnCls} data-testid="add-maternity-btn">Open maternity case</button>
        </>} />

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-8">
            <KpiCard label="Cases" value={num(items.length)} onClick={() => {}} testid="mat-kpi-total" />
            <KpiCard label="Antenatal" value={num(items.filter((i) => i.status === "antenatal").length)} onClick={() => {}} testid="mat-kpi-antenatal" />
            <KpiCard label="Delivered" value={num(items.filter((i) => i.delivery).length)} tone="success" onClick={() => {}} testid="mat-kpi-delivered" />
            <KpiCard label="High risk" value={num(items.filter((i) => i.high_risk).length)} tone="danger" onClick={() => {}} testid="mat-kpi-highrisk" />
          </div>

          <Table testid="maternity-table" columns={["Mother", "G / P", "LMP", "EDD", "Gestation", "Babies", "Status", "Actions"]}
            rows={items} empty={<EmptyState title="No maternity cases" subtitle="Open a maternity case for an admitted or antenatal patient." testid="maternity-empty" />}
            renderRow={(m) => (
              <tr key={m.maternity_id} data-testid={`maternity-row-${m.maternity_id}`}>
                <Td className="font-medium text-slate-900">{m.patient_name}
                  <span className="block text-xs text-slate-400">{m.patient_uhid}{m.high_risk ? " · High risk" : ""}</span></Td>
                <Td className="text-slate-600">G{m.gravida || 0} P{m.para || 0}</Td>
                <Td className="text-slate-600">{m.lmp_date ? dmy(m.lmp_date) : "—"}</Td>
                <Td className="text-slate-600">{m.edd ? dmy(m.edd) : "—"}</Td>
                <Td className="text-slate-600">{m.delivery ? "Delivered" : gaWeeks(m.lmp_date)}</Td>
                <Td className="text-slate-600">{(m.delivery?.babies || []).length}</Td>
                <Td><StatusPill status={m.status} /></Td>
                <Td>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openBill(m)} className={ghostBtnCls} data-testid={`mat-bill-${m.maternity_id}`}>
                      <IndianRupee className="h-3.5 w-3.5" /> Package &amp; Bill</button>
<button onClick={() => { setVisit({ weight: "", bp: "", haemoglobin: "", fundal_height: "", fetal_heart_rate: "", symptoms: "", examination: "", notes: "" }); setModal({ kind: "visit", record: m }); }}
                       className={ghostBtnCls} data-testid={`anc-visit-${m.maternity_id}`}><HeartPulse className="h-3.5 w-3.5" /> ANC visit</button>
                    <button onClick={() => { setEditClinical(m); setEditClinicalForm({ diagnosis: m.diagnosis || "", symptoms: m.symptoms || "", examination: m.examination || "", notes: m.notes || "", obstetric_history: m.obstetric_history || "", medical_history: m.medical_history || "", antenatal_notes: m.antenatal_notes || "" }); }}
                      className={ghostBtnCls} data-testid={`mat-edit-clinical-${m.maternity_id}`}><Pencil className="h-3.5 w-3.5" /> Edit clinical details</button>
                    <button onClick={() => setModal({ kind: "investigation", record: m })} className={ghostBtnCls}
                      data-testid={`investigation-${m.maternity_id}`}><FlaskConical className="h-3.5 w-3.5" /> Investigation</button>
                    {!m.delivery && (
                      <>
                        <button onClick={() => setModal({ kind: "labour", record: m })} className={ghostBtnCls} data-testid={`labour-${m.maternity_id}`}>Labour</button>
                        <button onClick={() => { setDelivery({ ...delivery, delivery_at: new Date().toISOString().slice(0, 16), babies: [{ ...EMPTY_BABY }] }); setModal({ kind: "delivery", record: m }); }}
                          className={primaryBtnCls} data-testid={`delivery-${m.maternity_id}`}><Baby className="h-3.5 w-3.5" /> Delivery</button>
                      </>
                    )}
                    {m.delivery && !m.discharge && (
                      <>
                        <button onClick={() => setModal({ kind: "postpartum", record: m })} className={ghostBtnCls} data-testid={`postpartum-${m.maternity_id}`}>Postpartum</button>
                        <button onClick={() => setModal({ kind: "discharge", record: m })} className={ghostBtnCls} data-testid={`mat-discharge-${m.maternity_id}`}>Discharge</button>
                      </>
                    )}
                    <button onClick={() => openPreview(m, "case")} className={ghostBtnCls} data-testid={`mat-case-sheet-${m.maternity_id}`}>
                      <FileText className="h-3.5 w-3.5" /> Case sheet</button>
                    {m.delivery && (
                      <button onClick={() => openPreview(m, "delivery")} className={ghostBtnCls} data-testid={`mat-delivery-record-${m.maternity_id}`}>
                        <Printer className="h-3.5 w-3.5" /> Delivery record</button>
                    )}
                    {m.discharge && (
                      <button onClick={() => openPreview(m, "discharge")} className={ghostBtnCls} data-testid={`mat-discharge-summary-${m.maternity_id}`}>
                        <Printer className="h-3.5 w-3.5" /> Discharge summary</button>
                    )}
                  </div>
                </Td>
              </tr>
            )} />
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} wide title="Open maternity case"
        subtitle="Antenatal profile for a registered female patient." testid="add-maternity-modal">
        <form onSubmit={create} className="grid gap-5 sm:grid-cols-3">
          <Field label="Mother (patient) *" className="sm:col-span-2">
            <select className={inputCls} value={form.patient_id} data-testid="maternity-patient-select"
              onChange={(e) => setForm({ ...form, patient_id: e.target.value })}>
              <option value="">Select patient</option>
              {patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.name} · {p.uhid}</option>)}
            </select>
          </Field>
          <Field label="Consultant">
            <select className={inputCls} value={form.doctor_id} data-testid="maternity-doctor-select"
              onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}>
              <option value="">Unassigned</option>
              {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>)}
            </select>
          </Field>
          <Field label="LMP date *"><input type="date" className={inputCls} data-testid="maternity-lmp-input" value={form.lmp_date}
            onChange={(e) => setForm({ ...form, lmp_date: e.target.value, edd: eddFromLmp(e.target.value) })} /></Field>
          <Field label="EDD (auto)"><input type="date" className={inputCls} data-testid="maternity-edd-input" value={form.edd}
            onChange={(e) => setForm({ ...form, edd: e.target.value })} /></Field>
          <Field label="Blood group">
            <select className={inputCls} value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })}>
              <option value="">Not known</option>
              {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          {[["gravida", "Gravida"], ["para", "Para"], ["living_children", "Living children"], ["abortions", "Abortions / miscarriages"]].map(([k, l]) => (
            <Field key={k} label={l}><input type="number" min="0" className={inputCls} data-testid={`maternity-${k}-input`}
              value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></Field>
          ))}
          <Field label="Allergies"><input className={inputCls} value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></Field>
          <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm">
            <input type="checkbox" checked={form.high_risk} data-testid="maternity-highrisk-checkbox"
              onChange={(e) => setForm({ ...form, high_risk: e.target.checked })} className="h-4 w-4 accent-[#0F172A]" />
            High-risk pregnancy
          </label>
          <Field label="Obstetric history" className="sm:col-span-3"><textarea rows={2} className={inputCls} data-testid="maternity-obhistory-input" value={form.obstetric_history} onChange={(e) => setForm({ ...form, obstetric_history: e.target.value })} /></Field>
          <Field label="Relevant medical history" className="sm:col-span-3"><textarea rows={2} className={inputCls} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} /></Field>
          <Field label="Antenatal notes" className="sm:col-span-3"><textarea rows={2} className={inputCls} value={form.antenatal_notes} onChange={(e) => setForm({ ...form, antenatal_notes: e.target.value })} /></Field>
          {formError && <p className="sm:col-span-3 text-sm font-medium text-red-600" data-testid="maternity-form-error">{formError}</p>}
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="maternity-submit-btn">Open case</button></div>
        </form>
      </Modal>

      <Modal
        open={directOpen}
        onClose={() => setDirectOpen(false)}
        wide
        title="Direct delivery (emergency shortcut)"
        subtitle="Use only for a delivery-only case without a prior antenatal record. This creates the maternity admission, records the delivery and issues the invoice in one step."
        testid="direct-delivery-modal"
      >
        <form onSubmit={submitDirectDelivery} className="space-y-6">
          <div className="flex gap-2" data-testid="direct-delivery-patient-mode">
            {["existing", "new"].map((m) => (
              <button type="button" key={m} onClick={() => setDirectForm({ ...directForm, patient_mode: m })}
                data-testid={`direct-delivery-mode-${m}`}
                className={`rounded-full px-4 py-1.5 text-sm font-medium ${directForm.patient_mode === m ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                {m === "existing" ? "Existing patient" : "Register new patient"}
              </button>
            ))}
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            {directForm.patient_mode === "existing" ? (
            <Field label="Mother (patient) *" className="sm:col-span-2">
              <select
                className={inputCls}
                data-testid="direct-delivery-patient-select"
                value={directForm.patient_id}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  patient_id: event.target.value,
                })}
              >
                <option value="">Select patient</option>
                {patients.filter((patient) => patient.gender === "female").map((patient) => (
                  <option key={patient.patient_id} value={patient.patient_id}>
                    {patient.name} · {patient.uhid}
                  </option>
                ))}
              </select>
            </Field>
            ) : (
            <>
              <Field label="Mother's full name *"><input className={inputCls} data-testid="dd-np-name" value={directForm.new_patient.name} onChange={(e) => setDirectForm({ ...directForm, new_patient: { ...directForm.new_patient, name: e.target.value } })} /></Field>
              <Field label="Mobile *"><input className={inputCls} data-testid="dd-np-phone" value={directForm.new_patient.phone} onChange={(e) => setDirectForm({ ...directForm, new_patient: { ...directForm.new_patient, phone: e.target.value } })} /></Field>
              <Field label="City *"><input className={inputCls} data-testid="dd-np-city" value={directForm.new_patient.city} onChange={(e) => setDirectForm({ ...directForm, new_patient: { ...directForm.new_patient, city: e.target.value } })} /></Field>
              <Field label="Date of birth"><input type="date" className={inputCls} data-testid="dd-np-dob" value={directForm.new_patient.dob} onChange={(e) => setDirectForm({ ...directForm, new_patient: { ...directForm.new_patient, dob: e.target.value } })} /></Field>
              <Field label="Husband / guardian name"><input className={inputCls} data-testid="dd-np-guardian" value={directForm.new_patient.guardian_name} onChange={(e) => setDirectForm({ ...directForm, new_patient: { ...directForm.new_patient, guardian_name: e.target.value } })} /></Field>
              <Field label="Emergency contact no."><input className={inputCls} data-testid="dd-np-emergency" value={directForm.new_patient.emergency_contact_phone} onChange={(e) => setDirectForm({ ...directForm, new_patient: { ...directForm.new_patient, emergency_contact_phone: e.target.value } })} /></Field>
            </>
            )}
            <Field label="Consultant">
              <select
                className={inputCls}
                data-testid="direct-delivery-doctor-select"
                value={directForm.doctor_id}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  doctor_id: event.target.value,
                })}
              >
                <option value="">Unassigned</option>
                {doctors.map((doctor) => (
                  <option key={doctor.doctor_id} value={doctor.doctor_id}>
                    Dr. {doctor.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Maternity package *" className="sm:col-span-2">
              <select
                required
                className={inputCls}
                data-testid="direct-delivery-package-select"
                value={directForm.package_id}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  package_id: event.target.value,
                })}
              >
                <option value="">Select an active package</option>
                {packages.filter((item) => item.active !== false).map((item) => (
                  <option key={item.package_id} value={item.package_id}>
                    {item.name} — {inr(item.price)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ward / bed (room)">
              <select className={inputCls} data-testid="direct-delivery-bed-select" value={directForm.bed_id}
                onChange={(e) => setDirectForm({ ...directForm, bed_id: e.target.value })}>
                <option value="">No bed / not admitted</option>
                {beds.map((b) => <option key={b.bed_id} value={b.bed_id}>{b.ward_name} · {b.bed_number}</option>)}
              </select>
            </Field>
            <Field label="Assigned nurse / staff">
              <select className={inputCls} data-testid="direct-delivery-nurse-select" value={directForm.nurse_user_id}
                onChange={(e) => setDirectForm({ ...directForm, nurse_user_id: e.target.value })}>
                <option value="">Unassigned</option>
                {staff.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {titleize(u.role || "")}</option>)}
              </select>
            </Field>
            <Field label="Gravida"><input type="number" min="0" className={inputCls} data-testid="dd-gravida" value={directForm.gravida} onChange={(e) => setDirectForm({ ...directForm, gravida: e.target.value })} /></Field>
            <Field label="Para"><input type="number" min="0" className={inputCls} data-testid="dd-para" value={directForm.para} onChange={(e) => setDirectForm({ ...directForm, para: e.target.value })} /></Field>
            <Field label="Blood group"><input className={inputCls} data-testid="dd-blood" value={directForm.blood_group} onChange={(e) => setDirectForm({ ...directForm, blood_group: e.target.value })} /></Field>
            <Field label="LMP date"><input type="date" className={inputCls} data-testid="dd-lmp" value={directForm.lmp_date} onChange={(e) => setDirectForm({ ...directForm, lmp_date: e.target.value })} /></Field>
            <Field label="Obstetric / medical history" className="sm:col-span-2"><input className={inputCls} data-testid="dd-obs-history" value={directForm.obstetric_history} onChange={(e) => setDirectForm({ ...directForm, obstetric_history: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="dd-high-risk" checked={directForm.high_risk} onChange={(e) => setDirectForm({ ...directForm, high_risk: e.target.checked })} /> High-risk pregnancy</label>
            <Field label="Discount (₹)"><input type="number" min="0" className={inputCls} data-testid="dd-discount" value={directForm.discount} onChange={(e) => setDirectForm({ ...directForm, discount: e.target.value })} /></Field>
            <Field label="Extra tax % (optional)"><input type="number" min="0" className={inputCls} data-testid="dd-tax" value={directForm.tax_percent} onChange={(e) => setDirectForm({ ...directForm, tax_percent: e.target.value })} /></Field>
            <Field label="Delivery date & time *">
              <input
                required
                type="datetime-local"
                className={inputCls}
                data-testid="direct-delivery-datetime-input"
                value={directForm.delivery_at}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  delivery_at: event.target.value,
                })}
              />
            </Field>
            <Field label="Mode of delivery *">
              <select
                className={inputCls}
                data-testid="direct-delivery-type-select"
                value={directForm.delivery_type}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  delivery_type: event.target.value,
                })}
              >
                {["normal", "c_section", "assisted", "other"].map((type) => (
                  <option key={type} value={type}>{titleize(type)}</option>
                ))}
              </select>
            </Field>
            <Field label="Outcome">
              <select
                className={inputCls}
                data-testid="direct-delivery-outcome-select"
                value={directForm.outcome}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  outcome: event.target.value,
                })}
              >
                {["live_birth", "still_birth", "neonatal_death", "other"].map((type) => (
                  <option key={type} value={type}>{titleize(type)}</option>
                ))}
              </select>
            </Field>
            <Field label="Attending doctor / midwife" className="sm:col-span-3">
              <input
                className={inputCls}
                data-testid="direct-delivery-attending-input"
                value={directForm.attending_doctor}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  attending_doctor: event.target.value,
                })}
              />
            </Field>
          </div>

          <div className="space-y-3">
            <p className="overline text-[#EA580C]">Newborn record</p>
            {directForm.babies.map((baby, index) => (
              <div
                key={index}
                className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-4"
                data-testid={`direct-delivery-baby-${index}`}
              >
                {["name", "weight_kg", "condition"].map((key) => (
                  <Field key={key} label={{ name: "Baby name", weight_kg: "Weight (kg)", condition: "Condition" }[key]}>
                    <input
                      className={inputCls}
                      data-testid={`direct-delivery-baby-${key}-${index}`}
                      value={baby[key]}
                      onChange={(event) => setDirectForm({
                        ...directForm,
                        babies: directForm.babies.map((item, itemIndex) => itemIndex === index
                          ? { ...item, [key]: event.target.value }
                          : item),
                      })}
                    />
                  </Field>
                ))}
                <Field label="Gender">
                  <select
                    className={inputCls}
                    data-testid={`direct-delivery-baby-gender-${index}`}
                    value={baby.gender}
                    onChange={(event) => setDirectForm({
                      ...directForm,
                      babies: directForm.babies.map((item, itemIndex) => itemIndex === index
                        ? { ...item, gender: event.target.value }
                        : item),
                    })}
                  >
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <label className="sm:col-span-4 flex items-center gap-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={baby.create_patient}
                    data-testid={`direct-delivery-baby-create-patient-${index}`}
                    onChange={(event) => setDirectForm({
                      ...directForm,
                      babies: directForm.babies.map((item, itemIndex) => itemIndex === index
                        ? { ...item, create_patient: event.target.checked }
                        : item),
                    })}
                  />
                  Create a linked newborn patient record
                </label>
              </div>
            ))}
            <button
              type="button"
              className={ghostBtnCls}
              data-testid="direct-delivery-add-baby-btn"
              onClick={() => setDirectForm({
                ...directForm,
                babies: [...directForm.babies, { ...EMPTY_BABY }],
              })}
            >
              Add another baby
            </button>
          </div>

          <div className="space-y-3">
            <p className="overline text-[#EA580C]">Advance / installment collection</p>
            {directForm.initial_payments.map((payment, index) => (
              <div
                key={index}
                className="grid gap-3 sm:grid-cols-3"
                data-testid={`direct-delivery-payment-${index}`}
              >
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputCls}
                  placeholder="Amount (optional)"
                  data-testid={`direct-delivery-payment-amount-${index}`}
                  value={payment.amount}
                  onChange={(event) => setDirectForm({
                    ...directForm,
                    initial_payments: directForm.initial_payments.map((item, itemIndex) => itemIndex === index
                      ? { ...item, amount: event.target.value }
                      : item),
                  })}
                />
                <select
                  className={inputCls}
                  data-testid={`direct-delivery-payment-mode-${index}`}
                  value={payment.mode}
                  onChange={(event) => setDirectForm({
                    ...directForm,
                    initial_payments: directForm.initial_payments.map((item, itemIndex) => itemIndex === index
                      ? { ...item, mode: event.target.value }
                      : item),
                  })}
                >
                  {["cash", "upi", "card", "bank", "cheque", "insurance", "other"].map((mode) => (
                    <option key={mode} value={mode}>{titleize(mode)}</option>
                  ))}
                </select>
                <input
                  className={inputCls}
                  placeholder="Reference"
                  data-testid={`direct-delivery-payment-reference-${index}`}
                  value={payment.reference_no}
                  onChange={(event) => setDirectForm({
                    ...directForm,
                    initial_payments: directForm.initial_payments.map((item, itemIndex) => itemIndex === index
                      ? { ...item, reference_no: event.target.value }
                      : item),
                  })}
                />
              </div>
            ))}
            <button
              type="button"
              className={ghostBtnCls}
              data-testid="direct-delivery-add-payment-btn"
              onClick={() => setDirectForm({
                ...directForm,
                initial_payments: [
                  ...directForm.initial_payments,
                  { amount: "", mode: "upi", reference_no: "" },
                ],
              })}
            >
              Add payment component
            </button>
          </div>
          <button className={primaryBtnCls} data-testid="direct-delivery-submit-btn">
            Record delivery & issue invoice
          </button>
        </form>
      </Modal>

      <Modal open={modal?.kind === "visit"} onClose={() => setModal(null)} wide title="Antenatal visit"
        subtitle={modal?.record?.patient_name} testid="anc-visit-modal">
        <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/hospital/maternity/${modal.record.maternity_id}/visits`, visit), "ANC visit recorded."); }}
          className="grid gap-5 sm:grid-cols-3">
          {[["weight", "Weight (kg)"], ["bp", "Blood pressure"], ["haemoglobin", "Haemoglobin"],
            ["fundal_height", "Fundal height"], ["fetal_heart_rate", "Fetal heart rate"]].map(([k, l]) => (
            <Field key={k} label={l}><input className={inputCls} data-testid={`anc-${k}-input`} value={visit[k]}
              onChange={(e) => setVisit({ ...visit, [k]: e.target.value })} /></Field>
          ))}
          <Field label="Symptoms" className="sm:col-span-3"><input className={inputCls} value={visit.symptoms} onChange={(e) => setVisit({ ...visit, symptoms: e.target.value })} /></Field>
          <Field label="Examination" className="sm:col-span-3"><textarea rows={2} className={inputCls} value={visit.examination} onChange={(e) => setVisit({ ...visit, examination: e.target.value })} /></Field>
          <Field label="Clinical notes" className="sm:col-span-3"><textarea rows={2} className={inputCls} data-testid="anc-notes-input" value={visit.notes} onChange={(e) => setVisit({ ...visit, notes: e.target.value })} /></Field>
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="anc-submit-btn">Save visit</button></div>
        </form>
      </Modal>

      <Modal open={modal?.kind === "investigation"} onClose={() => setModal(null)} title="Add investigation"
        subtitle={modal?.record?.patient_name} testid="investigation-modal">
        <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/hospital/maternity/${modal.record.maternity_id}/investigations`, inv), "Investigation recorded."); }}
          className="grid gap-5 sm:grid-cols-2">
          <Field label="Type">
            <select className={inputCls} value={inv.kind} data-testid="investigation-kind-select" onChange={(e) => setInv({ ...inv, kind: e.target.value })}>
              {["blood", "urine", "ultrasound", "other"].map((k) => <option key={k} value={k}>{titleize(k)}</option>)}
            </select>
          </Field>
          <Field label="Name *"><input required className={inputCls} data-testid="investigation-name-input" value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} /></Field>
          <Field label="Taken on"><input type="date" className={inputCls} value={inv.taken_on} onChange={(e) => setInv({ ...inv, taken_on: e.target.value })} /></Field>
          <Field label="Result"><input className={inputCls} data-testid="investigation-result-input" value={inv.result} onChange={(e) => setInv({ ...inv, result: e.target.value })} /></Field>
          <Field label="Notes" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={inv.notes} onChange={(e) => setInv({ ...inv, notes: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="investigation-submit-btn">Save investigation</button></div>
        </form>
      </Modal>

      <Modal open={modal?.kind === "labour"} onClose={() => setModal(null)} title="Labour observations"
        subtitle={modal?.record?.patient_name} testid="labour-modal">
        <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/hospital/maternity/${modal.record.maternity_id}/labour`, labour), "Labour status recorded."); }}
          className="grid gap-5 sm:grid-cols-2">
          <Field label="Labour status"><input className={inputCls} data-testid="labour-status-input" placeholder="Active first stage" value={labour.labour_status} onChange={(e) => setLabour({ ...labour, labour_status: e.target.value })} /></Field>
          <Field label="Cervical findings"><input className={inputCls} data-testid="labour-cervix-input" value={labour.cervical_findings} onChange={(e) => setLabour({ ...labour, cervical_findings: e.target.value })} /></Field>
          <Field label="Fetal monitoring" className="sm:col-span-2"><input className={inputCls} value={labour.fetal_monitoring} onChange={(e) => setLabour({ ...labour, fetal_monitoring: e.target.value })} /></Field>
          <Field label="Doctor / midwife notes" className="sm:col-span-2"><textarea rows={3} className={inputCls} value={labour.notes} onChange={(e) => setLabour({ ...labour, notes: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="labour-submit-btn">Save labour notes</button></div>
        </form>
      </Modal>

      <Modal open={modal?.kind === "delivery"} onClose={() => setModal(null)} wide title="Delivery record"
        subtitle={modal?.record?.patient_name} testid="delivery-modal">
        <form onSubmit={(e) => { e.preventDefault();
          run(() => api.post(`/hospital/maternity/${modal.record.maternity_id}/delivery`, {
            ...delivery, delivery_at: new Date(delivery.delivery_at).toISOString(),
            babies: delivery.babies.filter((b) => b.gender) }), "Delivery recorded."); }}
          className="space-y-6">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Date & time *"><input required type="datetime-local" className={inputCls} data-testid="delivery-datetime-input" value={delivery.delivery_at} onChange={(e) => setDelivery({ ...delivery, delivery_at: e.target.value })} /></Field>
            <Field label="Mode of delivery *">
              <select className={inputCls} value={delivery.delivery_type} data-testid="delivery-type-select" onChange={(e) => setDelivery({ ...delivery, delivery_type: e.target.value })}>
                {["normal", "c_section", "assisted", "other"].map((t) => <option key={t} value={t}>{titleize(t)}</option>)}
              </select>
            </Field>
            <Field label="Outcome">
              <select className={inputCls} value={delivery.outcome} onChange={(e) => setDelivery({ ...delivery, outcome: e.target.value })}>
                {["live_birth", "still_birth", "neonatal_death", "other"].map((t) => <option key={t} value={t}>{titleize(t)}</option>)}
              </select>
            </Field>
            <Field label="Indication"><input className={inputCls} value={delivery.indication} onChange={(e) => setDelivery({ ...delivery, indication: e.target.value })} /></Field>
            <Field label="Complications"><input className={inputCls} value={delivery.complications} onChange={(e) => setDelivery({ ...delivery, complications: e.target.value })} /></Field>
            <Field label="Attending doctor / midwife"><input className={inputCls} data-testid="delivery-attending-input" value={delivery.attending_doctor} onChange={(e) => setDelivery({ ...delivery, attending_doctor: e.target.value })} /></Field>
            <Field label="Delivery notes" className="sm:col-span-3"><textarea rows={2} className={inputCls} value={delivery.notes} onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })} /></Field>
          </div>
          <div className="space-y-4">
            <p className="overline text-[#EA580C]">Newborn record</p>
            {delivery.babies.map((b, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid={`baby-${idx}`}>
                <div className="grid gap-4 sm:grid-cols-4">
                  <Field label="Baby name"><input className={inputCls} data-testid={`baby-name-${idx}`} value={b.name}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) })} /></Field>
                  <Field label="Gender *">
                    <select className={inputCls} value={b.gender} data-testid={`baby-gender-${idx}`}
                      onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, gender: e.target.value } : x) })}>
                      <option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Birth weight (kg)"><input className={inputCls} data-testid={`baby-weight-${idx}`} value={b.weight_kg}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, weight_kg: e.target.value } : x) })} /></Field>
                  <Field label="Birth time"><input type="time" className={inputCls} value={b.birth_time}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, birth_time: e.target.value } : x) })} /></Field>
                  <Field label="Apgar 1 min"><input className={inputCls} value={b.apgar_1}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, apgar_1: e.target.value } : x) })} /></Field>
                  <Field label="Apgar 5 min"><input className={inputCls} value={b.apgar_5}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, apgar_5: e.target.value } : x) })} /></Field>
                  <Field label="Newborn status" className="sm:col-span-2"><input className={inputCls} value={b.condition}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, condition: e.target.value } : x) })} /></Field>
                </div>
                <label className="mt-3 flex items-center gap-3 text-sm">
                  <input type="checkbox" checked={b.create_patient} data-testid={`baby-create-patient-${idx}`}
                    onChange={(e) => setDelivery({ ...delivery, babies: delivery.babies.map((x, i) => i === idx ? { ...x, create_patient: e.target.checked } : x) })}
                    className="h-4 w-4 accent-[#0F172A]" />
                  Create a linked patient record (own UHID) for this newborn
                </label>
              </div>
            ))}
            <button type="button" onClick={() => setDelivery({ ...delivery, babies: [...delivery.babies, { ...EMPTY_BABY }] })}
              className={ghostBtnCls} data-testid="add-baby-btn">Add another baby</button>
          </div>
          <button className={primaryBtnCls} data-testid="delivery-submit-btn">Save delivery record</button>
        </form>
      </Modal>

      <Modal open={modal?.kind === "postpartum"} onClose={() => setModal(null)} title="Postpartum care"
        subtitle={modal?.record?.patient_name} testid="postpartum-modal">
        <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/hospital/maternity/${modal.record.maternity_id}/postpartum`, pp), "Postpartum care recorded."); }}
          className="grid gap-5 sm:grid-cols-2">
          <Field label="Observations" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="pp-observations-input" value={pp.observations} onChange={(e) => setPp({ ...pp, observations: e.target.value })} /></Field>
          <Field label="Bleeding"><input className={inputCls} value={pp.bleeding} onChange={(e) => setPp({ ...pp, bleeding: e.target.value })} /></Field>
          <Field label="Pain"><input className={inputCls} value={pp.pain} onChange={(e) => setPp({ ...pp, pain: e.target.value })} /></Field>
          <Field label="Medications"><input className={inputCls} value={pp.medications} onChange={(e) => setPp({ ...pp, medications: e.target.value })} /></Field>
          <Field label="Breastfeeding"><input className={inputCls} value={pp.breastfeeding} onChange={(e) => setPp({ ...pp, breastfeeding: e.target.value })} /></Field>
          <label className="sm:col-span-2 flex items-center gap-3 text-sm">
            <input type="checkbox" checked={pp.discharge_ready} data-testid="pp-ready-checkbox"
              onChange={(e) => setPp({ ...pp, discharge_ready: e.target.checked })} className="h-4 w-4 accent-[#0F172A]" />
            Mother is ready for discharge
          </label>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="pp-submit-btn">Save postpartum care</button></div>
        </form>
      </Modal>

      <Modal open={modal?.kind === "discharge"} onClose={() => setModal(null)} wide title="Maternity discharge"
        subtitle={modal?.record?.patient_name} testid="mat-discharge-modal">
        <form onSubmit={(e) => { e.preventDefault(); const rec = modal.record; run(() => api.post(`/hospital/maternity/${rec.maternity_id}/discharge`, dis), "Maternity discharge recorded."); }}
          className="grid gap-5 sm:grid-cols-2">
          <Field label="Mother — summary" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-mother-summary-input" value={dis.mother_summary} onChange={(e) => setDis({ ...dis, mother_summary: e.target.value })} /></Field>
          <Field label="Mother — medications"><input className={inputCls} value={dis.mother_medications} onChange={(e) => setDis({ ...dis, mother_medications: e.target.value })} /></Field>
          <Field label="Mother — follow-up"><input className={inputCls} value={dis.mother_follow_up} onChange={(e) => setDis({ ...dis, mother_follow_up: e.target.value })} /></Field>
          <Field label="Baby — condition"><input className={inputCls} data-testid="mat-baby-condition-input" value={dis.baby_condition} onChange={(e) => setDis({ ...dis, baby_condition: e.target.value })} /></Field>
          <Field label="Baby — feeding instructions"><input className={inputCls} value={dis.baby_feeding} onChange={(e) => setDis({ ...dis, baby_feeding: e.target.value })} /></Field>
          <Field label="Baby — vaccination"><input className={inputCls} value={dis.baby_vaccination} onChange={(e) => setDis({ ...dis, baby_vaccination: e.target.value })} /></Field>
          <Field label="Baby — follow-up"><input className={inputCls} value={dis.baby_follow_up} onChange={(e) => setDis({ ...dis, baby_follow_up: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="mat-discharge-submit-btn">Save discharge record</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(billFor)} onClose={() => { setBillFor(null); setBillData(null); }} wide
        title="Maternity package & bill" subtitle={billFor?.patient_name} testid="mat-bill-modal">
        {!billData ? <Loading /> : (
          <div className="space-y-6" data-testid="mat-bill-body">
            {(() => {
              const pkg = billData.maternity?.package;
              const extras = billData.maternity?.extras || [];
              const inv = billData.invoice;
              const activePackages = packages.filter((p) => p.active !== false);
              return (
                <>
                  <div className="rounded-lg border border-slate-200 p-4">
                    <p className="overline text-[#EA580C] mb-3">Package</p>
                    <div className="flex flex-wrap items-end gap-3">
                      <Field label="Assign / change package" className="flex-1 min-w-[220px]">
                        <select className={inputCls} data-testid="mat-package-select" value={pkg?.package_id || ""}
                          onChange={(e) => assignPackage(e.target.value)}>
                          <option value="">Select a package</option>
                          {activePackages.map((p) => <option key={p.package_id} value={p.package_id}>{p.name} — {inr(p.price)}</option>)}
                        </select>
                      </Field>
                    </div>
                    {pkg ? (
                      <div className="mt-4 rounded-md bg-slate-50 p-4" data-testid="mat-package-summary">
                        <div className="flex items-baseline justify-between">
                          <p className="font-semibold text-slate-900">{pkg.name}</p>
                          <p className="font-semibold text-slate-900" data-testid="mat-package-price">{inr(pkg.price)}</p>
                        </div>
                        {(pkg.inclusions || []).length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-slate-500">Package includes:</p>
                            <ul className="mt-1 flex flex-wrap gap-1.5">
                              {(pkg.inclusions || []).map((i, idx) => (
                                <li key={idx} className="rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-xs text-slate-600">{i}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {pkg.assigned_by && <p className="mt-2 text-xs text-slate-400">Assigned by {pkg.assigned_by}{pkg.assigned_by_shift ? ` — ${pkg.assigned_by_shift}` : ""}</p>}
                      </div>
                    ) : <p className="mt-3 text-sm text-slate-400">No package assigned yet.</p>}
                  </div>

                  <div className="rounded-lg border border-slate-200 p-4">
                    <p className="overline text-[#EA580C] mb-3">Extra services (billed separately)</p>
                    {extras.length === 0 ? <p className="text-sm text-slate-400 mb-3">No extra services added.</p> : (
                      <ul className="mb-3 divide-y divide-slate-100" data-testid="mat-extras-list">
                        {extras.map((ex) => (
                          <li key={ex.extra_id} className="flex items-center justify-between py-2 text-sm" data-testid={`mat-extra-${ex.extra_id}`}>
                            <span className={ex.status === "reversed" ? "text-slate-400 line-through" : "text-slate-700"}>{ex.description} <span className="text-slate-400">× {ex.quantity}</span>
                              {ex.status === "reversed" && <span className="block text-xs text-red-500 no-underline">Reversed · {ex.reversal?.reason}</span>}
                              {ex.status !== "reversed" && ex.by_name && <span className="block text-xs text-slate-400">Added by {ex.by_name}{ex.by_shift ? ` — ${ex.by_shift}` : ""}</span>}</span>
                            <span className="flex items-center gap-3">
                              <span className={ex.status === "reversed" ? "text-slate-400" : "text-slate-800"}>{inr(ex.line_total || ex.amount * ex.quantity)}</span>
                              {ex.status !== "reversed" && <button onClick={() => { setReverseExtra(ex); setReverseExtraReason(""); }} className="text-slate-400 hover:text-red-600" title="Reverse" data-testid={`mat-extra-remove-${ex.extra_id}`}><Trash2 className="h-4 w-4" /></button>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <form onSubmit={addExtra} className="grid gap-3 sm:grid-cols-4 items-end">
                      <Field label="From master (optional)" className="sm:col-span-2">
                        <select className={inputCls} data-testid="mat-extra-service-select" value={extraForm.service_id}
                          onChange={(e) => pickExtraService(e.target.value)}>
                          <option value="">Custom charge</option>
                          {extraServices.filter((s) => s.active !== false).map((s) => <option key={s.service_id} value={s.service_id}>{s.name} — {inr(s.charge)}</option>)}
                        </select>
                      </Field>
                      <Field label="Description"><input className={inputCls} data-testid="mat-extra-desc" value={extraForm.description} onChange={(e) => setExtraForm({ ...extraForm, description: e.target.value })} /></Field>
                      <Field label="Amount (₹)"><input type="number" className={inputCls} data-testid="mat-extra-amount" value={extraForm.amount} onChange={(e) => setExtraForm({ ...extraForm, amount: e.target.value })} /></Field>
                      <div className="sm:col-span-4"><button className={ghostBtnCls} data-testid="mat-extra-add-btn"><Plus className="h-4 w-4" /> Add extra service</button></div>
                    </form>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="overline text-[#EA580C]">Bill</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openNurse(billFor)} className={ghostBtnCls} data-testid="mat-nurse-btn"><HeartPulse className="h-3.5 w-3.5" /> Care staff</button>
                        {can("hospital.billing.manage") && <button onClick={genInvoice} className={ghostBtnCls} data-testid="mat-generate-invoice-btn">{inv ? "Regenerate bill" : "Generate bill"}</button>}
                      </div>
                    </div>
                    {!inv ? <p className="text-sm text-slate-400">Assign a package, then generate the bill.</p> : (
                      <div className="space-y-2 text-sm" data-testid="mat-invoice-summary">
                        <p className="text-xs text-slate-400">Invoice {inv.invoice_no}</p>
                        {inv.items.map((l, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-slate-700">{l.description}{l.detail ? <span className="block text-xs text-slate-400">{l.detail}</span> : null}</span>
                            <span className="text-slate-800">{inr(l.amount)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold"><span>Total</span><span data-testid="mat-invoice-total">{inr(inv.total)}</span></div>
                        <div className="flex justify-between text-emerald-700"><span>Paid</span><span data-testid="mat-invoice-paid">{inr(inv.paid)}</span></div>
                        <div className="flex justify-between text-red-600"><span>Due</span><span data-testid="mat-invoice-due">{inr(inv.outstanding)}</span></div>
                        <button
                          type="button"
                          className={ghostBtnCls}
                          data-testid="mat-invoice-receipt-btn"
                          onClick={() => setReceipt(billData)}
                        >
                          <Printer className="h-3.5 w-3.5" /> Patient receipt
                        </button>
                        {(inv.payments || []).length > 0 && (
                          <ul className="mt-2 border-t border-slate-100 pt-2 space-y-1">
                            {inv.payments.map((p) => (
                              <li key={p.payment_id} className="flex justify-between text-xs text-slate-500">
                                <span>{titleize(p.mode)}{p.by_name ? ` · ${p.by_name}` : ""}{p.by_shift ? ` (${p.by_shift})` : ""}</span>
                                <span>{inr(p.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {can("hospital.payments.manage") && inv.outstanding > 0 && (
                          <form onSubmit={collectPayment} className="mt-3 grid gap-3 sm:grid-cols-4 items-end border-t border-slate-100 pt-3">
                            <Field label="Amount (₹)"><input type="number" className={inputCls} data-testid="mat-pay-amount" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></Field>
                            <Field label="Mode">
                              <select className={inputCls} data-testid="mat-pay-mode" value={payForm.mode} onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })}>
                                {["cash", "upi", "card", "bank", "cheque", "insurance"].map((m) => <option key={m} value={m}>{titleize(m)}</option>)}
                              </select>
                            </Field>
                            <Field label="Reference"><input className={inputCls} value={payForm.reference_no} onChange={(e) => setPayForm({ ...payForm, reference_no: e.target.value })} /></Field>
                            <div><button className={primaryBtnCls} data-testid="mat-pay-submit">Record payment</button></div>
                          </form>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      <Modal open={Boolean(reverseExtra)} onClose={() => { setReverseExtra(null); setReverseExtraReason(""); }}
        title="Reverse extra charge" subtitle="Non-destructive — the original entry stays in history and is excluded from the bill." testid="mat-reverse-extra-modal">
        <form onSubmit={submitReverseExtra} className="space-y-4">
          <p className="text-sm text-slate-600">Reversing <strong>{reverseExtra?.description}</strong> ({inr(reverseExtra?.amount)}). A reason is mandatory.</p>
          <Field label="Reversal reason *"><textarea required rows={3} className={inputCls} data-testid="mat-reverse-extra-reason" value={reverseExtraReason} onChange={(e) => setReverseExtraReason(e.target.value)} /></Field>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setReverseExtra(null); setReverseExtraReason(""); }} className={ghostBtnCls}>Cancel</button>
            <button className={primaryBtnCls} data-testid="mat-reverse-extra-confirm">Confirm reversal</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(nurseFor)} onClose={() => setNurseFor(null)} wide title="Nurse / care staff assignment"
        subtitle="Reassigning keeps the previous assignment as a closed history record — never overwritten." testid="mat-nurse-modal">
        <form onSubmit={submitNurse} className="grid gap-4 sm:grid-cols-3" data-testid="mat-nurse-form">
          <Field label="Staff member *">
            <select required className={inputCls} data-testid="mat-nurse-select" value={nurseForm.staff_user_id}
              onChange={(e) => setNurseForm({ ...nurseForm, staff_user_id: e.target.value })}>
              <option value="">Select staff</option>
              {staff.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {titleize(u.role || "")}</option>)}
            </select>
          </Field>
          <Field label="Role / shift label"><input className={inputCls} data-testid="mat-nurse-role" value={nurseForm.role_label} onChange={(e) => setNurseForm({ ...nurseForm, role_label: e.target.value })} /></Field>
          <Field label="Reason"><input className={inputCls} data-testid="mat-nurse-reason" value={nurseForm.reason} onChange={(e) => setNurseForm({ ...nurseForm, reason: e.target.value })} /></Field>
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="mat-nurse-assign-btn">Assign staff</button></div>
        </form>
        <div className="mt-5 space-y-2" data-testid="mat-nurse-history">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assignment history</p>
          {nurseHistory.length === 0 && <p className="text-sm text-slate-400">No assignments yet.</p>}
          {nurseHistory.map((h) => (
            <div key={h.assignment_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span className="text-slate-800">{h.staff_name} <span className="text-slate-400">· {h.role_label}</span></span>
              <span className="text-xs text-slate-500">{dmy(h.start_at)} → {h.end_at ? dmy(h.end_at) : <span className="text-emerald-600 font-medium">Active</span>}</span>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={pkgOpen} onClose={() => setPkgOpen(false)} wide title="Maternity packages & extra services"
        subtitle="Configure package pricing and reusable extra services. Internal cost allocation is never shown on the patient bill." testid="mat-pkg-manage-modal">
        <div className="flex gap-2 mb-5">
          <button onClick={() => setPkgTab("packages")} className={pkgTab === "packages" ? primaryBtnCls : ghostBtnCls} data-testid="pkg-tab-packages">Packages</button>
          <button onClick={() => setPkgTab("services")} className={pkgTab === "services" ? primaryBtnCls : ghostBtnCls} data-testid="pkg-tab-services">Extra services</button>
        </div>
        {pkgTab === "packages" ? (
          <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={savePackage} className="space-y-4" data-testid="pkg-form">
              <p className="text-sm font-semibold text-slate-900">{pkgForm.package_id ? "Edit package" : "New package"}</p>
              <Field label="Package name *"><input required className={inputCls} data-testid="pkg-name-input" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Price (₹) *"><input required type="number" className={inputCls} data-testid="pkg-price-input" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} /></Field>
                <Field label="Delivery type">
                  <select className={inputCls} value={pkgForm.delivery_type} onChange={(e) => setPkgForm({ ...pkgForm, delivery_type: e.target.value })}>
                    {["normal", "c_section", "assisted", "other"].map((t) => <option key={t} value={t}>{titleize(t)}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Room category (optional)"><input className={inputCls} value={pkgForm.room_category} placeholder="Deluxe" onChange={(e) => setPkgForm({ ...pkgForm, room_category: e.target.value })} /></Field>
              <Field label="Inclusions (one per line)"><textarea rows={4} className={inputCls} data-testid="pkg-inclusions-input" placeholder={"Room\nNursing care\nDelivery procedure\nRoutine medicines\nDoctor care"} value={pkgForm.inclusions} onChange={(e) => setPkgForm({ ...pkgForm, inclusions: e.target.value })} /></Field>
              <Field label="Description"><textarea rows={2} className={inputCls} value={pkgForm.description} onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })} /></Field>
              <div className="flex gap-2">
                <button className={primaryBtnCls} data-testid="pkg-save-btn">{pkgForm.package_id ? "Save changes" : "Add package"}</button>
                {pkgForm.package_id && <button type="button" onClick={() => setPkgForm(EMPTY_PKG)} className={ghostBtnCls}>New</button>}
              </div>
            </form>
            <div className="space-y-2" data-testid="pkg-list">
              {packages.length === 0 && <p className="text-sm text-slate-400">No packages yet.</p>}
              {packages.map((p) => (
                <div key={p.package_id} className="rounded-lg border border-slate-200 p-3.5" data-testid={`pkg-item-${p.package_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{p.name} {p.active === false && <span className="text-xs text-slate-400">(inactive)</span>}</p>
                      <p className="text-xs text-slate-500">{inr(p.price)} · {titleize(p.delivery_type || "normal")}</p>
                    </div>
                    <button onClick={() => setPkgForm({ package_id: p.package_id, name: p.name, price: p.price, delivery_type: p.delivery_type || "normal", room_category: p.room_category || "", inclusions: (p.inclusions || []).join("\n"), description: p.description || "" })}
                      className={ghostBtnCls} data-testid={`pkg-edit-${p.package_id}`}>Edit</button>
                  </div>
                  {(p.inclusions || []).length > 0 && <p className="mt-1 text-xs text-slate-400">Includes: {(p.inclusions || []).join(", ")}</p>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={saveService} className="space-y-4" data-testid="svc-form">
              <p className="text-sm font-semibold text-slate-900">{svcForm.service_id ? "Edit extra service" : "New extra service"}</p>
              <Field label="Service name *"><input required className={inputCls} data-testid="svc-name-input" value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Charge (₹) *"><input required type="number" className={inputCls} data-testid="svc-charge-input" value={svcForm.charge} onChange={(e) => setSvcForm({ ...svcForm, charge: e.target.value })} /></Field>
                <Field label="Tax %"><input type="number" className={inputCls} value={svcForm.tax_percent} onChange={(e) => setSvcForm({ ...svcForm, tax_percent: e.target.value })} /></Field>
              </div>
              <Field label="Description"><input className={inputCls} value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} /></Field>
              <div className="flex gap-2">
                <button className={primaryBtnCls} data-testid="svc-save-btn">{svcForm.service_id ? "Save changes" : "Add extra service"}</button>
                {svcForm.service_id && <button type="button" onClick={() => setSvcForm(EMPTY_SVC)} className={ghostBtnCls}>New</button>}
              </div>
            </form>
            <div className="space-y-2" data-testid="svc-list">
              {extraServices.length === 0 && <p className="text-sm text-slate-400">No extra services yet.</p>}
              {extraServices.map((s) => (
                <div key={s.service_id} className="rounded-lg border border-slate-200 p-3.5 flex items-center justify-between" data-testid={`svc-item-${s.service_id}`}>
                  <div>
                    <p className="font-medium text-slate-900">{s.name} {s.active === false && <span className="text-xs text-slate-400">(inactive)</span>}</p>
                    <p className="text-xs text-slate-500">{inr(s.charge)}{s.tax_percent ? ` + ${s.tax_percent}% tax` : ""}</p>
                  </div>
                  <button onClick={() => setSvcForm({ service_id: s.service_id, name: s.name, charge: s.charge, tax_percent: s.tax_percent || "", description: s.description || "" })}
                    className={ghostBtnCls} data-testid={`svc-edit-${s.service_id}`}>Edit</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(editClinical)} onClose={() => setEditClinical(null)} wide
        title="Edit clinical details"
        subtitle="Correct the provisional diagnosis, symptoms, examination or notes for this maternity case."
        testid="maternity-edit-clinical-modal">
        <form onSubmit={(e) => {
          e.preventDefault();
          if (editClinicalBusy) return;
          setEditClinicalBusy(true);
          api.patch(`/hospital/maternity/${editClinical.maternity_id}`, editClinicalForm)
            .then(() => { toast.success("Clinical details updated."); setEditClinical(null); load(); })
            .catch((err) => toast.error(apiError(err)))
            .finally(() => setEditClinicalBusy(false));
        }} className="grid gap-5 sm:grid-cols-2">
          <Field label="Provisional diagnosis" className="sm:col-span-2"><input className={inputCls} data-testid="mat-edit-clinical-diagnosis" value={editClinicalForm.diagnosis} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, diagnosis: e.target.value })} /></Field>
          <Field label="Symptoms" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-edit-clinical-symptoms" value={editClinicalForm.symptoms} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, symptoms: e.target.value })} /></Field>
          <Field label="Examination" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-edit-clinical-examination" value={editClinicalForm.examination} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, examination: e.target.value })} /></Field>
          <Field label="Obstetric history" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-edit-clinical-obstetric" value={editClinicalForm.obstetric_history} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, obstetric_history: e.target.value })} /></Field>
          <Field label="Medical history" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-edit-clinical-medical" value={editClinicalForm.medical_history} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, medical_history: e.target.value })} /></Field>
          <Field label="Antenatal notes" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-edit-clinical-antenatal" value={editClinicalForm.antenatal_notes} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, antenatal_notes: e.target.value })} /></Field>
          <Field label="Notes" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="mat-edit-clinical-notes" value={editClinicalForm.notes} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, notes: e.target.value })} /></Field>
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={() => setEditClinical(null)} className={ghostBtnCls}>Cancel</button>
            <button disabled={editClinicalBusy} className={primaryBtnCls} data-testid="mat-edit-clinical-submit">
              {editClinicalBusy ? "Saving…" : "Save clinical details"}
            </button>
          </div>
        </form>
      </Modal>

      <PrintPreview open={Boolean(preview)} onClose={() => setPreview(null)}
        title={{ case: "Maternity case sheet", delivery: "Delivery record", discharge: "Maternity discharge summary" }[previewKind]}
        subtitle={preview?.maternity?.patient_name} testid="maternity-print-preview">
        <MaternityDoc data={preview} kind={previewKind} />
      </PrintPreview>

      <PrintPreview
        open={Boolean(receipt)}
        onClose={() => setReceipt(null)}
        title={`Invoice ${receipt?.invoice?.invoice_no || ""}`}
        subtitle="Patient receipt"
        testid="maternity-invoice-receipt"
      >
        <HospitalInvoiceDoc
          invoice={receipt?.invoice}
          patient={receipt?.patient}
          business={receipt?.business}
        />
      </PrintPreview>
    </div>
  );
}
